import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../../MainURL';
import { getAdminSession } from '../../adminSession';
import '../../Admin.scss';

type AreaRow = {
  area: number;
  name: string;
  isDefault: boolean;
  createdAt: string | null;
  churchCount: number;
  databases: string[];
  dbHost: string | null;
  uploadDir: string;
  tables: { appChurch: number; appRoll: number; appFinance: number; appSundayschool: number } | null;
};

type ApiEnvelope<T> = { ok: boolean; data?: T; message?: string };

function errorMessageOf(err: unknown, fallback: string): string {
  const data = axios.isAxiosError(err) ? err.response?.data : null;
  if (data && typeof data === 'object' && 'message' in data) {
    return String((data as { message?: string }).message) || fallback;
  }
  return fallback;
}

// area = 교회 데이터 DB(appChurch/appRoll/appFinance/appSundayschool)와 업로드 폴더 묶음.
// 교회가 늘어나면 새 area를 열고 "신규 교회 기본 area"로 지정해, 그 뒤로 등록되는 교회가 새 DB에 저장되게 한다.
export default function AreaManagementPage() {
  const session = getAdminSession();
  const [items, setItems] = useState<AreaRow[]>([]);
  const [names, setNames] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const fetchItems = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await axios.get<ApiEnvelope<AreaRow[]>>(`${MainURL}/admin-console/areas`, {
        params: { requesterId: session.id },
      });
      if (res.data?.ok && res.data.data) {
        setItems(res.data.data);
        setNames(Object.fromEntries(res.data.data.map((a) => [a.area, a.name])));
      } else {
        setErrorMsg(res.data?.message || 'area 목록을 불러오지 못했습니다.');
      }
    } catch (err) {
      setErrorMsg(errorMessageOf(err, 'area 목록을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleCreate = async () => {
    if (!session) return;
    const nextArea = items.reduce((max, a) => Math.max(max, a.area), 0) + 1;
    const name = window.prompt(
      `area ${nextArea}를 새로 엽니다. DB 4개(appChurch_${nextArea}, appRoll_${nextArea}, appFinance_${nextArea}, appSundayschool_${nextArea})와 ` +
        `업로드 폴더(uploads/area${nextArea}/)가 만들어집니다.\n` +
        '새 area는 열기만 하고 기본 area는 바뀌지 않습니다. 신규 교회를 여기에 넣으려면 목록에서 "기본으로 지정"을 눌러 주세요.\n\n' +
        'area 이름(선택):',
      `area ${nextArea}`,
    );
    if (name === null) return;
    setSavingKey('create');
    try {
      const res = await axios.post<ApiEnvelope<{ area: number }>>(`${MainURL}/admin-console/areas`, {
        requesterId: session.id,
        name,
      });
      if (!res.data?.ok) throw new Error(res.data?.message);
      fetchItems();
    } catch (err) {
      alert(errorMessageOf(err, 'area를 만들지 못했습니다.'));
    } finally {
      setSavingKey(null);
    }
  };

  const handleUpdate = async (area: number, patch: { name?: string; isDefault?: boolean }) => {
    if (!session) return;
    if (
      patch.isDefault &&
      !window.confirm(`앞으로 등록(결제·수동 등록)되는 교회는 area ${area}에 저장됩니다. 이미 등록된 교회는 그대로입니다. 계속할까요?`)
    ) {
      return;
    }
    setSavingKey(`area-${area}`);
    try {
      const res = await axios.put<ApiEnvelope<null>>(`${MainURL}/admin-console/areas/${area}`, {
        requesterId: session.id,
        ...patch,
      });
      if (!res.data?.ok) throw new Error(res.data?.message);
      fetchItems();
    } catch (err) {
      alert(errorMessageOf(err, '저장하지 못했습니다.'));
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="Main-cover admin-panel">
      <div className="admin-panel-header">
        <div>
          <h2 className="admin-panel-title">area 관리</h2>
          <p className="admin-panel-desc">
            교회 데이터(예배·설교·소식·갤러리·교적·출석·재정·주일학교)와 업로드 파일을 area 단위로 나눠 저장합니다. 교회가
            어느 정도 늘어나면 새 area를 열고 &quot;기본으로 지정&quot;하세요. 그 뒤로 등록되는 교회만 새 area에 저장되고, 이미
            등록된 교회는 옮겨지지 않습니다. 교회 앱을 빌드할 때는 tenantConfig.ts의 APP_AREA를 앱관리에 표시된 그 교회의 area와
            같은 값으로 넣어야 합니다.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="admin-refresh-btn" onClick={handleCreate} disabled={savingKey === 'create'}>
            {savingKey === 'create' ? '만드는 중...' : '+ 새 area 열기'}
          </button>
          <button type="button" className="admin-refresh-btn" onClick={fetchItems} disabled={loading}>
            {loading ? '불러오는 중...' : '새로고침'}
          </button>
        </div>
      </div>

      {errorMsg && <p className="admin-error-text">{errorMsg}</p>}

      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>area</th>
              <th>이름</th>
              <th>교회 수</th>
              <th>DB (테이블 수)</th>
              <th>DB 서버</th>
              <th>업로드 폴더</th>
              <th>신규 교회</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading ? (
              <tr>
                <td colSpan={8}>area 정보가 없습니다.</td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.area}>
                  <td>{item.area}</td>
                  <td>
                    <input
                      type="text"
                      value={names[item.area] ?? ''}
                      onChange={(e) => setNames((prev) => ({ ...prev, [item.area]: e.target.value }))}
                      style={{ width: 120 }}
                    />
                  </td>
                  <td>{item.churchCount}</td>
                  <td>
                    {item.databases.map((db) => {
                      const base = db.replace(/_\d+$/, '') as keyof NonNullable<AreaRow['tables']>;
                      const count = item.tables ? item.tables[base] : null;
                      return (
                        <div key={db} className="admin-subtext">
                          {db} ({count ?? '확인 실패'})
                        </div>
                      );
                    })}
                  </td>
                  <td>{item.dbHost || '기본 DB 서버'}</td>
                  <td>{item.uploadDir}</td>
                  <td>
                    {item.isDefault ? (
                      <span className="admin-status-tag admin-status-tag--active">기본 area</span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="admin-btn edit-btn"
                        onClick={() => handleUpdate(item.area, { name: names[item.area] ?? '' })}
                        disabled={savingKey === `area-${item.area}` || (names[item.area] ?? '') === item.name}
                      >
                        이름 저장
                      </button>
                      {!item.isDefault && (
                        <button
                          type="button"
                          className="admin-btn edit-btn"
                          onClick={() => handleUpdate(item.area, { isDefault: true })}
                          disabled={savingKey === `area-${item.area}`}
                        >
                          기본으로 지정
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
